import { useEffect, useRef, useState } from "react";

/**
 * Drop-in replacement for `<img>` with two robustness layers:
 *
 *   1. Lazy load via IntersectionObserver — the actual `src` is only set
 *      once the element is within 200px of the viewport. This keeps
 *      large reference / library grids from firing 20+ simultaneous
 *      thumbnail requests on mount, which used to saturate the backend
 *      and trigger browser timeouts → broken images.
 *
 *   2. Auto-retry on load failure — when the storage CDN is still
 *      replicating a freshly-uploaded file, the first GET can 404. The
 *      browser never retries an `<img>` on its own. We retry ONCE after
 *      a comfortable 1500ms delay (typical CDN propagation window),
 *      WITHOUT cache-busting the first time so the browser can reuse
 *      any partial response. Only on the second failure do we
 *      cache-bust, and we give up after that.
 *
 * Both behaviours are opt-out via props:
 *   - `lazy={false}` → fetch immediately on mount (use for above-the-fold
 *     hero images).
 *   - `maxRetries={0}` → never retry (use for non-critical decoration).
 *
 * Forwards every other prop to the underlying `<img>` so it stays a
 * faithful drop-in.
 */
export default function RetryImage({
    src,
    lazy = true,
    maxRetries = 1,
    onError,
    onLoad,
    rootMargin = "200px",
    ...rest
}) {
    // 0 = original URL · 1 = retry without bust · 2 = retry with bust.
    const [attempt, setAttempt] = useState(0);
    const [inView, setInView] = useState(!lazy);
    const imgRef = useRef(null);
    const timerRef = useRef(null);

    // Reset retry budget whenever the underlying src changes.
    useEffect(() => {
        setAttempt(0);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [src]);

    // IntersectionObserver — fire src only when (near) viewport.
    useEffect(() => {
        if (!lazy) {
            setInView(true);
            return;
        }
        if (!imgRef.current || inView) return;
        const el = imgRef.current;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) {
                        setInView(true);
                        io.disconnect();
                        break;
                    }
                }
            },
            { rootMargin },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [lazy, inView, rootMargin]);

    // Build the actual src for this attempt.
    // - attempt 0: original URL (lets the browser cache normally).
    // - attempt 1: same URL again (the browser is forced to refetch
    //   because the previous request errored — no bust needed).
    // - attempt 2+: cache-bust `?_r=N`, because some browsers cache
    //   the failed response and won't refetch the same URL.
    //
    // While the element is still out-of-view OR `src` is missing we
    // serve a 1×1 transparent placeholder. **NEVER** pass `src=""`:
    // browsers interpret an empty string as the current document URL,
    // re-download the whole page, and that DOM thrash collides with
    // React's reconciliation → `insertBefore` crashes when a sibling
    // component (e.g. ChevronDown inside the refs-strip toggle)
    // re-renders during a generation.
    const TRANSPARENT_PX =
        "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
    let actualSrc = TRANSPARENT_PX;
    if (inView && src) {
        if (attempt <= 1) {
            actualSrc = src;
        } else {
            actualSrc = `${src}${src.includes("?") ? "&" : "?"}_r=${attempt}`;
        }
    }

    const handleError = (e) => {
        if (attempt >= maxRetries) {
            onError?.(e);
            return;
        }
        // Wait 1500 ms before retrying. Plenty for CDN propagation; not
        // so much that the user notices. We don't backoff exponentially
        // because a single retry is what fixes the post-upload race in
        // 99% of cases.
        const delay = 1500;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setAttempt((n) => n + 1);
        }, delay);
    };

    return (
        <img
            ref={imgRef}
            // Native browser hints: even before our IntersectionObserver
            // fires we tell the browser this image is lazy + can decode
            // off the main thread. Browsers with `loading="lazy"` will
            // skip the network request entirely until the element is
            // about to enter the viewport, which compounds with our
            // own observer when many images are stacked in a chat.
            loading={lazy ? "lazy" : "eager"}
            decoding="async"
            {...rest}
            src={actualSrc}
            onError={handleError}
            onLoad={(e) => {
                if (timerRef.current) clearTimeout(timerRef.current);
                onLoad?.(e);
            }}
        />
    );
}
